/**
 * webPush.js — browser-side push subscription helpers.
 *
 * Handles the four things every frontend needs to do to make Web Push
 * actually work on iPhone:
 *   1. Ask for Notification permission (only after a user gesture — Apple
 *      denies persistently if you prompt on page load).
 *   2. Register the service worker (index.js already does this too, but
 *      we `.ready` here so we don't race).
 *   3. Fetch the VAPID public key from the backend, then subscribe via
 *      pushManager.subscribe().
 *   4. POST the subscription to /api/push/subscribe so the backend can
 *      target this device later.
 *
 * The failure mode we care about most: on iPhone, `pushManager.subscribe`
 * throws if the app isn't running as an installed PWA (Home Screen). We
 * detect that and surface a friendly error the UI can show.
 *
 * See /app/backend/routes/push.py for the server side.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Base64url → Uint8Array. Required by PushManager.subscribe. */
function urlBase64ToUint8Array(b64) {
    const padding = "=".repeat((4 - (b64.length % 4)) % 4);
    const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

/** Detect whether the current page is running as an installed PWA. */
export function isStandalonePWA() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)")?.matches ||
        window.navigator.standalone === true
    );
}

/** Detect iPhone / iPad. */
export function isIOS() {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
}

/** True if this browser can subscribe to push at all. */
export function isPushSupported() {
    return (
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

/**
 * Return the current push permission + subscription snapshot.
 * Used by NotificationsPanel to render the toggle in the right state.
 */
export async function getPushState() {
    if (!isPushSupported()) {
        return { supported: false, permission: "unsupported", subscribed: false };
    }
    const permission = Notification.permission; // "default" | "granted" | "denied"
    let subscribed = false;
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        subscribed = !!sub;
    } catch (_) { /* ignore */ }
    return {
        supported: true,
        permission,
        subscribed,
        standalone: isStandalonePWA(),
        ios: isIOS(),
    };
}

/**
 * Subscribe this device to push. Must be called from a user gesture on iOS.
 *
 * Returns:
 *   { ok: true, endpoint }  on success
 *   { ok: false, reason }   on failure (reason is a short code the UI maps)
 *       reasons: unsupported | not_pwa_ios | denied | vapid_missing | subscribe_failed
 */
export async function enableWebPush(crewId) {
    if (!isPushSupported()) return { ok: false, reason: "unsupported" };
    // iOS Safari refuses to hand out a subscription unless the app is
    // running from the Home Screen. Detect early so we can show a
    // "Add to Home Screen first" nudge instead of a cryptic error.
    if (isIOS() && !isStandalonePWA()) {
        return { ok: false, reason: "not_pwa_ios" };
    }
    let permission = Notification.permission;
    if (permission === "default") {
        permission = await Notification.requestPermission();
    }
    if (permission !== "granted") {
        return { ok: false, reason: "denied" };
    }

    // Grab the VAPID public key from the backend so we don't hard-code
    // it in the JS bundle (makes rotation possible without a redeploy —
    // though we never rotate; see routes/push.py docstring).
    let vapidKey = "";
    try {
        const r = await axios.get(`${API}/push/vapid-public-key`);
        vapidKey = r.data?.public_key || "";
    } catch (_) {
        return { ok: false, reason: "vapid_missing" };
    }
    if (!vapidKey) return { ok: false, reason: "vapid_missing" };

    try {
        const reg = await navigator.serviceWorker.ready;
        // If there's already a subscription, drop it and re-subscribe.
        // Rationale: the existing sub may have been created against a
        // DIFFERENT applicationServerKey (from an earlier install of the
        // SW), which means every push we sign with our current VAPID
        // private key will 400/403 with no user-visible signal. Force a
        // clean subscription that matches OUR current public key.
        //
        // 2026-02-27 dedupe fix: also POST /push/unsubscribe with the
        // OLD endpoint BEFORE re-subscribing, so the backend drops the
        // stale row. Without this, Mongo keeps both the old + new
        // endpoint under the same crew_id and every push fans out
        // twice (double-alert bug on sign-in). Backend has a belt-and
        // -suspenders same-user_agent sweep too.
        const existing = await reg.pushManager.getSubscription();
        if (existing) {
            const staleEndpoint = existing.endpoint;
            try { await existing.unsubscribe(); } catch (_) { /* ignore */ }
            try {
                await axios.post(`${API}/push/unsubscribe`, {
                    crew_id: crewId,
                    endpoint: staleEndpoint,
                });
            } catch (_) { /* backend cleanup best-effort */ }
        }
        const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
        const raw = sub.toJSON();
        // Guard: make sure we actually have the keys before POSTing (some
        // older browsers return undefined for keys on stale subs).
        if (!raw?.keys?.p256dh || !raw?.keys?.auth) {
            return { ok: false, reason: "subscribe_failed", detail: "browser returned incomplete subscription" };
        }
        await axios.post(`${API}/push/subscribe`, {
            crew_id: crewId,
            endpoint: raw.endpoint,
            keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
            user_agent: navigator.userAgent || "",
            standalone: isStandalonePWA(),
        });
        return { ok: true, endpoint: raw.endpoint };
    } catch (e) {
        return { ok: false, reason: "subscribe_failed", detail: String(e).slice(0, 200) };
    }
}

/** Unsubscribe this device from push + drop the row on the backend. */
export async function disableWebPush(crewId) {
    if (!isPushSupported()) return { ok: false, reason: "unsupported" };
    try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) return { ok: true };
        const endpoint = sub.endpoint;
        try { await sub.unsubscribe(); } catch (_) { /* ignore */ }
        try {
            await axios.post(`${API}/push/unsubscribe`, {
                crew_id: crewId,
                endpoint,
            });
        } catch (_) { /* ignore */ }
        return { ok: true };
    } catch (e) {
        return { ok: false, reason: "unsubscribe_failed", detail: String(e).slice(0, 200) };
    }
}

/** Fire a test push via the backend so the user can verify their phone. */
export async function sendTestPush(crewId) {
    try {
        const r = await axios.post(`${API}/push/test`, {
            crew_id: crewId,
            title: "WoW test push",
            body: "If you see this, notifications are working on this device.",
        });
        // Pass through EVERY diagnostic field the backend returns so the UI
        // can craft a precise error message (VAPID missing / stale sub /
        // send failure / no subs on file).
        return { ok: true, ...(r.data || {}) };
    } catch (e) {
        return { ok: false, detail: String(e).slice(0, 200) };
    }
}

/** Fetch the backend's view of this device's push subscription rows.
 *  Handy for showing "you have 1 device registered" in Settings and
 *  for diagnosing "sent 0 with no dead cleanup" edge cases. */
export async function getPushStatusFromServer(crewId) {
    try {
        const r = await axios.get(`${API}/push/status/${crewId}`);
        return { ok: true, ...(r.data || {}) };
    } catch (e) {
        return { ok: false, detail: String(e).slice(0, 200) };
    }
}


